import { css } from "@emotion/css";
import {
  Box,
  Button,
  Colors,
  DatePicker,
  OutlinedInput,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputProps as MuiInputProps,
} from "@analyticsMD/quil";
import moment from "moment";
import EventIcon from "@mui/icons-material/Event";

import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useMutation, useQueryClient } from "react-query";
import { hideIntelligenceText, patchEDD, postEDD } from "../api/endpoints";
import IntelligenceText from "../types/IntelligenceText";
import { Sources } from "../types/Sources";
import { TypeChoices } from "../types/TypeChoices";
import { ActionTrack } from "../types/Analytics";
import qCustomColors from "../colors";
import { deepPurple, grey } from "@mui/material/colors";
import EDD from "../types/EDD";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { Input, FormControl } from "@mui/material";

const sxStyles = {
  dayPicker: {
    display: "flex",
    padding: "0.5rem",
    "& .MuiPickersDay-root": {
      "&.Mui-selected": {
        backgroundColor: `${deepPurple[600]} !important`,
        boxShadow: `0 1px 3px 0 ${qCustomColors.purpleLight}`,
        borderRadius: "50%",
      },
    },
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  icon: {
    fontSize: ".5rem",
    marginLeft: "-0.5rem",
    color: `${grey[900]} !important`,
  },
  inlineIcon: {
    fontSize: ".5rem",
    marginRight: "1rem",
    color: `${grey[900]} !important`,
  },
  doneButton: {
    boxShadow: "none",
    fontSize: "0.9rem",
    color: deepPurple[800],
    backgroundColor: "transparent",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  doneButtonWrapper: {
    position: "absolute",
    right: "0rem",
    bottom: "0rem",
  },
  listItemText: {
    margin: 0,
    textAlign: "center",
  },
  ampmSection: {
    display: "flex",
    flexDirection: "column",
    width: "3rem",
    paddingLeft: "0.5rem",
    position: "relative",
  },
};

const styles = {
  input: css`
    cursor: pointer !important;
    border-radius: 4px;
    height: "2.5rem";
    width: 100%;
  `,
  inputProp: (isInline) => css`
    text-align: left;
    cursor: pointer;
    color: transparent !important;
    text-shadow: 0 0 0 ${Colors.grey_65};
    padding: ${isInline ? "0.61rem 0.875rem" : "0.67rem 0.875rem"} !important;
  `,
};

interface EDDSelectorProps {
  currentEDD: string;
  setCurrentEDD: Dispatch<SetStateAction<string>>;
  currentUserEDD?: EDD;
  patientID: number;
  isDischargeAM: boolean;
  setIsDischargeAM: Dispatch<SetStateAction<boolean>>;
  intelText?: IntelligenceText;
  enableDischargeTimeOfDay: boolean;
  isInline?: boolean;
  hasSuggestion?: boolean;
  analytics: AnalyticsBrowser;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

const EDDSelector: FC<EDDSelectorProps> = ({
  currentEDD,
  setCurrentEDD,
  currentUserEDD,
  patientID,
  isDischargeAM,
  setIsDischargeAM,
  intelText,
  enableDischargeTimeOfDay,
  isInline,
  hasSuggestion = false,
  analytics,
  setShowErrorToast,
  disabled,
}) => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  let currentSelectedEDD = null;
  if (currentUserEDD) {
    currentSelectedEDD = moment(currentUserEDD.edd_value);
  }
  useEffect(() => {
    if (currentUserEDD) setIsDischargeAM(currentUserEDD.is_am_discharge);
  }, [currentUserEDD, setIsDischargeAM]);

  // Also clear createdEddId when currentUserEDD updates (so we use the prop value)
  useEffect(() => {
    if (currentUserEDD) {
      setCreatedEddId(null); // Clear local state, use prop from now on
    }
  }, [currentUserEDD]);

  const [isOpen, setIsOpen] = useState(false);
  const [createdEddId, setCreatedEddId] = useState<number | null>(null);

  const patchMutation = useMutation(patchEDD, {
    onSuccess: (data) => {
      // Don't need to update optimistic data - server will provide real data
      queryClient.invalidateQueries({ queryKey: ["edd"] });
      setIsUpdating(false);

      // if we are in the patient list, we need to invalidate that as well
      if (isInline) {
        queryClient.invalidateQueries({
          queryKey: "eddlist",
        });
      }
    },
    onError: () => {
      // revert the change if the mutation fails
      setCurrentEDD(currentSelectedEDD);
      setIsUpdating(false);
      setShowErrorToast(true);
    },
  });

  const postMutation = useMutation(postEDD, {
    onSuccess: (data) => {
      setCreatedEddId(data.id); // Track the new EDD ID
      queryClient.invalidateQueries({ queryKey: ["edd"] });
      setIsUpdating(false);

      // if we are in the patient list, we need to invalidate that as well
      if (isInline) {
        queryClient.invalidateQueries({
          queryKey: "eddlist",
        });
      }
    },
    onError: () => {
      // revert the change if the mutation fails
      setCurrentEDD(currentSelectedEDD);
      setIsUpdating(false);
      setShowErrorToast(true);
    },
  });

  const hideIntelTextMutation = useMutation(hideIntelligenceText, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["intelligenceText"] });
    },
  });

  const handleSetEDD = (e, overrideIsAM?: boolean) => {
    if (isUpdating) return;
    if (!e) return;

    setIsUpdating(true);

    const edd = moment(e).format("YYYY-MM-DD");
    const isAM = overrideIsAM !== undefined ? overrideIsAM : isDischargeAM;

    // Check if we have an existing EDD (from props OR from a recent create)
    const existingEddId = currentUserEDD?.id || createdEddId;

    if (existingEddId) {
      patchMutation.mutate({
        id: existingEddId,
        edd_value: edd,
        status: currentUserEDD?.status || null,
        source: Sources.USER_CREATED,
        // clear intelligence fields
        suggested_patient_edd_id: null,
        decision_updated_ts: null,
        is_am_discharge: isAM,
      });
    } else {
      postMutation.mutate({
        edd_value: edd,
        source: Sources.USER_CREATED,
        edd_type: TypeChoices.USER_ENTERED,
        patient: patientID,
        suggested_patient_edd_id: null,
        status: null,
        is_am_discharge: isAM,
      });
    }

    // whenever we take an action, we also need to hide the intel text, if any
    if (intelText) {
      hideIntelTextMutation.mutate(intelText.id);
    }
    setCurrentEDD(e);

    analytics?.track(ActionTrack.EDD_INTERACTION, {
      edd_value: edd,
    });
  };

  const handleDischargeTimeChange = (isAM: boolean) => {
    setIsDischargeAM(isAM);
    // Only save if we have a date selected
    if (currentEDD || currentUserEDD) {
      handleSetEDD(currentEDD || currentSelectedEDD, isAM);
    }
  };

  const handleDoneClick = () => {
    setIsOpen(false);
  };

  const ActionList = () => {
    return (
      <Box sx={sxStyles.ampmSection}>
        <Box
          sx={{
            paddingLeft: "0.5rem",
          }}
        >
          <List sx={{ padding: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleDischargeTimeChange(true)}
                sx={{
                  padding: "0.5rem 0",
                  background: isDischargeAM ? deepPurple[600] : Colors.white,
                  color: isDischargeAM ? Colors.white : Colors.grey_80,
                  "&:hover, &.Mui-focusVisible": {
                    backgroundColor: isDischargeAM
                      ? deepPurple[600]
                      : "transparent",
                  },
                }}
              >
                <ListItemText primary={"AM"} sx={sxStyles.listItemText} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleDischargeTimeChange(false)}
                sx={{
                  padding: "0.5rem 0",
                  background: !isDischargeAM ? deepPurple[600] : Colors.white,
                  color: !isDischargeAM ? Colors.white : Colors.grey_80,
                  "&:hover, &.Mui-focusVisible": {
                    backgroundColor: !isDischargeAM
                      ? deepPurple[600]
                      : "transparent",
                  },
                }}
              >
                <ListItemText primary={"PM"} sx={sxStyles.listItemText} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Box sx={sxStyles.doneButtonWrapper}>
          <Button
            color="secondary"
            size="small"
            variant="text"
            sx={sxStyles.doneButton}
            onClick={handleDoneClick}
          >
            Done
          </Button>
        </Box>
      </Box>
    );
  };

  // shift the dates by 12 hours to move from AM -> PM for display
  const currentEDDWithMeridiem = !isDischargeAM
    ? moment(currentEDD).add(12, "hours")
    : moment(currentEDD);

  const currentSelectedEDDWithMeridiem = !isDischargeAM
    ? moment(currentSelectedEDD).add(12, "hours")
    : moment(currentSelectedEDD);

  const inputComponent = useCallback(
    (params) => {
      if (isInline) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Input
              style={{
                fontSize: "0.9rem",
                border: `1px solid ${Colors.grey_15}`,
                paddingLeft: "0.75rem",
              }}
              ref={params.inputRef}
              {...(params.inputProps as MuiInputProps)}
              onClick={(e) => {
                if (!disabled) {
                  setIsOpen(!isOpen);
                }
              }}
              className={styles.input}
              disableUnderline
              inputProps={{
                className: styles.inputProp(isInline),
                placeholder: "Select an EDD",
              }}
              endAdornment={
                <InputAdornment position="end" sx={sxStyles.inlineIcon}>
                  <EventIcon />
                </InputAdornment>
              }
            />
            {params.InputProps?.endAdornment}
          </Box>
        );
      }
      return (
        <Box sx={sxStyles.inputWrapper}>
          <FormControl variant="outlined" sx={{ width: "100%" }}>
            <InputLabel
              sx={{
                top: "9px",
                fontSize: "0.8rem",
                left: "12px",
              }}
              htmlFor="edd-input"
            >
              EDD
            </InputLabel>
            <OutlinedInput
              id="edd-input"
              ref={params.inputRef}
              {...(params.inputProps as MuiInputProps)}
              onClick={() => setIsOpen(!isOpen)}
              className={styles.input}
              style={{ fontSize: "0.9rem" }}
              label="EDD"
              notched
              inputProps={{
                className: styles.inputProp(isInline),
                placeholder: "Select an EDD",
              }}
              endAdornment={
                <InputAdornment position="end" sx={sxStyles.icon}>
                  <EventIcon />
                </InputAdornment>
              }
            />
            {params.InputProps?.endAdornment}
          </FormControl>
        </Box>
      );
    },
    [isInline, isOpen, setIsOpen, disabled]
  );

  return (
    <DatePicker
      inputFormat={enableDischargeTimeOfDay ? "E MM/dd/yy, aa" : "E MM/dd/yy"}
      value={
        currentEDD ? currentEDDWithMeridiem : currentSelectedEDDWithMeridiem
      }
      onChange={(e) => handleSetEDD(e)}
      disableHighlightToday
      disableOpenPicker
      disableMaskedInput
      disablePast
      disabled={disabled}
      closeOnSelect={!enableDischargeTimeOfDay}
      views={["day"]}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      components={{
        ActionBar: enableDischargeTimeOfDay ? ActionList : null,
      }}
      PaperProps={{
        sx: sxStyles.dayPicker,
      }}
      PopperProps={{
        placement: isInline ? "bottom" : "bottom-start",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, hasSuggestion ? 42 : 0],
            },
          },
        ],
      }}
      renderInput={inputComponent}
    />
  );
};

export default EDDSelector;
